import { Button, Form, InputNumber, Modal, Select, Space, message } from "antd";
import { useEffect, useMemo } from "react";
import { useMutation } from "react-query";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { ICampaignPromoCodesService } from "@shared/interfaces/campaign-promo-codes-service.interface";
import { CampaignPromoCodeDto } from "@shared/types/campaign-promo-codes/campaign-promo-code.dto";
import { CampaignDto } from "@shared/types/campaigns/campaign.dto";

interface GeneratePromoCodesModalProps {
  open: boolean;
  campaigns: CampaignDto[];
  defaultCampaignId?: string;
  onClose: () => void;
  onSuccess: (codes: CampaignPromoCodeDto[], campaignId: string) => void;
}

type GenerateFormValues = {
  campaignId: string;
  codeCount: number;
};

const getCampaignLabel = (campaign: CampaignDto) =>
  campaign.codePrefix
    ? `${campaign.name} (${campaign.codePrefix})`
    : campaign.name;

const GeneratePromoCodesModal = ({
  open,
  campaigns,
  defaultCampaignId,
  onClose,
  onSuccess,
}: GeneratePromoCodesModalProps) => {
  const [form] = Form.useForm<GenerateFormValues>();
  const promoService = appInject<ICampaignPromoCodesService>(
    DI_TOKENS.campaignPromoCodesService,
  );

  const campaignOptions = useMemo(
    () =>
      campaigns.map((c) => ({
        label: getCampaignLabel(c),
        value: c.id,
      })),
    [campaigns],
  );

  const generateMutation = useMutation(
    (values: GenerateFormValues) =>
      promoService.create({
        campaignId: values.campaignId,
        codeCount: values.codeCount,
      }),
    {
      onSuccess: (codes, values) => {
        onSuccess(codes, values.campaignId);
        onClose();
        form.resetFields();
      },
      onError: (error: unknown) => {
        const err = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const detail =
          err?.response?.data?.message ??
          err?.message ??
          (typeof error === "string" ? error : undefined);
        void message.error(detail ?? "Failed to generate promo codes");
      },
    },
  );

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        campaignId: defaultCampaignId,
        codeCount: 10,
      });
    }
  }, [open, defaultCampaignId, form]);

  const handleFinish = (values: GenerateFormValues) => {
    if (!Number.isInteger(values.codeCount)) {
      form.setFields([
        { name: "codeCount", errors: ["Please enter a whole number"] },
      ]);
      return;
    }
    generateMutation.mutate(values);
  };

  return (
    <Modal
      title="Generate promo codes"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ codeCount: 10 }}
      >
        <Form.Item
          name="campaignId"
          label="Campaign"
          rules={[{ required: true, message: "Select a campaign" }]}
        >
          <Select
            placeholder="Select campaign"
            options={campaignOptions}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="codeCount"
          label="Number of codes"
          rules={[
            { required: true, message: "Number of codes is required" },
            {
              type: "number",
              min: 1,
              max: 500,
              message: "Enter a value between 1 and 500",
            },
            {
              validator: (_, value) =>
                value == null || Number.isInteger(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error("Please enter a whole number")),
            },
          ]}
        >
          <InputNumber min={1} max={500} step={1} style={{ width: "100%" }} />
        </Form.Item>
        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={generateMutation.isLoading}
          >
            Generate
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default GeneratePromoCodesModal;
